import { useState, useEffect } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { fetchRewardsByIds } from "../../lib/reward-api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { confirmToast } from "../../lib/confirmToast";
import { formatPoints, formatProfession } from "../../lib/display";
import RewardPhoto from "../../components/RewardPhoto";
import { Search, Loader2, CheckCircle, XCircle, Clock, User, Coins, PackageCheck } from "lucide-react";

const statusConfig = {
  pendente: { label: "Pendente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  aprovado: { label: "Aprovado", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  pago: { label: "Pago", color: "bg-green-100 text-green-700", icon: CheckCircle },
  cancelado: { label: "Cancelado", color: "bg-red-100 text-red-700", icon: XCircle },
};

const AdminResgates = () => {
  const [resgates, setResgates] = useState([]);
  const [parceiros, setParceiros] = useState({});
  const [recompensas, setRecompensas] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resgatesRes, parceirosRes] = await Promise.all([
        api.get("/resgates"),
        api.get("/parceiros"),
      ]);
      const recompensasRes = await fetchRewardsByIds(resgatesRes.data.map((resgate) => resgate.recompensa_id), { admin: true });
      
      setResgates(resgatesRes.data);
      
      // Create lookup maps
      const parceirosMap = {};
      parceirosRes.data.forEach(p => parceirosMap[p.id] = p);
      setParceiros(parceirosMap);
      
      const recompensasMap = {};
      recompensasRes.data.forEach(r => recompensasMap[r.id] = r);
      setRecompensas(recompensasMap);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar dados."));
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (resgateId, newStatus) => {
    try {
      await api.put(`/resgates/${resgateId}/status?status=${newStatus}`);
      toast.success(`Resgate ${newStatus === "aprovado" ? "aprovado" : newStatus === "pago" ? "marcado como pago" : "cancelado"}!`);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao atualizar status."));
    }
  };

  const filteredResgates = resgates.filter((r) => {
    const parceiro = parceiros[r.parceiro_id];
    const recompensa = recompensas[r.recompensa_id];
    
    const matchesSearch = 
      parceiro?.nome.toLowerCase().includes(search.toLowerCase()) ||
      (r.recompensa_nome || recompensa?.nome || "").toLowerCase().includes(search.toLowerCase());
    
    const matchesFilter = filter === "all" || r.status === filter;
    
    return matchesSearch && matchesFilter;
  });

  const reconcileStock = (redemption) => confirmToast({
    title: "Confirmar baixa no Autcom?",
    description: "Confirme somente após registrar a saída deste produto no Autcom. A reserva local será conciliada com o estoque consultado no ERP.",
    confirmLabel: "Confirmar baixa",
    onConfirm: async () => {
      try {
        const response = await api.post(`/resgates/${redemption.id}/conciliar-estoque-erp`);
        toast.success(response.data.message);
        fetchData();
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Não foi possível confirmar a baixa."));
      }
    },
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-resgates">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Resgates</h1>
        <p className="text-slate-600 mt-1">Gerencie os pedidos de resgate de recompensas</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            placeholder="Buscar resgates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-10"
            data-testid="search-resgates-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "pendente", "aprovado", "pago", "cancelado"].map((status) => (
            <Button
              key={status}
              variant={filter === status ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? "btn-primary" : ""}
              data-testid={`filter-${status}`}
            >
              {status === "all" ? "Todos" : statusConfig[status]?.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Parceiro
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Recompensa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Pontos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredResgates.map((resgate) => {
                  const parceiro = parceiros[resgate.parceiro_id];
                  const recompensa = recompensas[resgate.recompensa_id];
                  const config = statusConfig[resgate.status];
                  const StatusIcon = config?.icon || Clock;
                  
                  return (
                    <tr key={resgate.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {parceiro?.nome || "Desconhecido"}
                            </p>
                            <p className="text-sm text-slate-500">
                              {formatProfession(parceiro?.profissao)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <RewardPhoto
                            src={recompensa?.foto_base64}
                            alt={recompensa?.nome || "Recompensa"}
                            className="h-10 w-10 shrink-0 rounded-lg"
                            iconClassName="h-5 w-5"
                          />
                          <span className="text-slate-900">
                            {resgate.recompensa_nome || recompensa?.nome || "Desconhecido"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <Coins className="h-4 w-4 text-yellow-500" />
                          <span className="font-semibold text-slate-900">
                            {formatPoints(resgate.pontos_utilizados)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {new Date(resgate.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config?.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          {config?.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {resgate.status === "pendente" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateStatus(resgate.id, "aprovado")}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`approve-resgate-${resgate.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Aprovar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateStatus(resgate.id, "cancelado")}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`cancel-resgate-${resgate.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Cancelar
                              </Button>
                            </>
                          )}
                          {resgate.status === "aprovado" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateStatus(resgate.id, "pago")}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`pay-resgate-${resgate.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Marcar como Pago
                            </Button>
                          )}
                          {(resgate.status === "pago" || resgate.status === "cancelado") && (
                            <span className="text-sm text-slate-400">
                              {resgate.status === "pago" ? "Concluído" : "Cancelado"}
                            </span>
                          )}
                          {resgate.origem_erp && resgate.status === "pago" && !resgate.estoque_erp_conciliado && <Button variant="outline" size="sm" onClick={() => reconcileStock(resgate)} data-testid={`reconcile-erp-${resgate.id}`}><PackageCheck className="mr-1 h-4 w-4" />Confirmar baixa ERP</Button>}
                          {resgate.estoque_erp_conciliado && <span className="text-xs text-green-700">Baixa ERP confirmada</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredResgates.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      {search || filter !== "all" 
                        ? "Nenhum resgate encontrado" 
                        : "Nenhum resgate realizado ainda"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminResgates;
